import type { TFunction } from 'i18next';

import { formatCalendarDate } from '@/shared/lib/datetime';

import { type CalendarEvent, splitLocal, timeRangeLabel } from './model';

/** `3 h 30 min`, `2 h`, `45 min`. */
export function formatMinutes(minutes: number, t: TFunction): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return t('calendar.workload.m', { m });
  return m === 0 ? t('calendar.workload.h', { h }) : t('calendar.workload.hm', { h, m });
}

/** "Thu, 8 Oct · 09:00–13:30", "8 Oct 22:00 – 9 Oct 02:00", "8 – 10 Oct", "Due 8 Oct, 18:00". */
export function describeWhen(event: CalendarEvent, locale: string, t: TFunction): string {
  const date = (value: string) => formatCalendarDate(value, locale);
  if (event.kind === 'deadline') {
    const day = date(event.start.slice(0, 10));
    return t('calendar.preview.dueOn', {
      when: event.allDay ? day : `${day}, ${splitLocal(event.start).time}`,
    });
  }
  if (event.end === null) return date(event.start.slice(0, 10));
  if (event.allDay) {
    const last = new Date(`${event.end}T00:00:00Z`);
    last.setUTCDate(last.getUTCDate() - 1);
    const lastDay = last.toISOString().slice(0, 10);
    return lastDay === event.start
      ? date(event.start)
      : t('calendar.preview.allDayRange', { from: date(event.start), to: date(lastDay) });
  }
  const from = splitLocal(event.start);
  const to = splitLocal(event.end);
  if (from.date === to.date) return `${date(from.date)} · ${timeRangeLabel(event) ?? ''}`;
  // An end at exactly midnight closes the previous day.
  return `${date(from.date)} ${from.time} – ${date(to.date)} ${to.time}`;
}
